import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

class AuthService {
  constructor() {
    this.token = localStorage.getItem('token');
    this.refreshToken = localStorage.getItem('refresh_token');
    this.user = JSON.parse(localStorage.getItem('user') || 'null');
  }

  /**
   * Login with email and password
   * @param {string} email 
   * @param {string} password 
   * @returns {Promise<Object>}
   */
  async login(email, password) {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        email,
        password
      });

      if (response.data.success) {
        this.setAuthData(response.data);
        return response.data;
      }
      throw new Error(response.data.message || 'Login failed');
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Login with Google OAuth using credential token
   * @param {string} credential - Google JWT credential token
   * @returns {Promise<Object>}
   */
  async loginWithGoogle(credential) {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/google_verify`, {
        credential: credential
      });

      if (response.data.success) {
        this.setAuthData(response.data);
        return response.data;
      }
      throw new Error(response.data.message || 'Google login failed');
    } catch (error) {
      console.error('Google login error:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Register a new user
   * @param {Object} userData 
   * @returns {Promise<Object>}
   */
  async signup(userData) {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/signup`, userData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Verify email with code
   * @param {string} email 
   * @param {string} verificationCode 
   * @returns {Promise<Object>}
   */
  async verifyEmail(email, verificationCode) {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/verify_email`, {
        email,
        verification_code: verificationCode
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Verify current session
   * @returns {Promise<Object>}
   */
  async verifySession() {
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/verify_session`, {
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      });
      
      if (response.data.success) {
        this.user = response.data.user;
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      
      return response.data;
    } catch (error) {
      this.logout();
      throw error.response?.data || error;
    }
  }

  /**
   * Refresh the authentication token
   * @returns {Promise<Object>}
   */
  async refreshAuthToken() {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/refresh_token`, {
        refresh_token: this.refreshToken
      });

      if (response.data.success) {
        this.token = response.data.token;
        this.refreshToken = response.data.refresh_token;
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('refresh_token', response.data.refresh_token);
      }

      return response.data;
    } catch (error) {
      this.logout();
      throw error.response?.data || error;
    }
  }

  /**
   * Forgot password - request reset code
   * @param {string} email 
   * @returns {Promise<Object>}
   */
  async forgotPassword(email) {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/forgot_password`, {
        email
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Reset password with code
   * @param {string} email 
   * @param {string} resetCode 
   * @param {string} newPassword 
   * @returns {Promise<Object>}
   */
  async resetPassword(email, resetCode, newPassword) {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/reset_password`, {
        email,
        reset_code: resetCode,
        new_password: newPassword
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Store authentication data
   * @param {Object} data 
   */
  setAuthData(data) {
    this.token = data.token;
    this.refreshToken = data.refresh_token;
    this.user = data.user;
    
    localStorage.setItem('token', data.token);
    localStorage.setItem('refresh_token', data.refresh_token);
    localStorage.setItem('user', JSON.stringify(data.user));
  }

  /**
   * Logout user
   */
  logout() {
    this.token = null;
    this.refreshToken = null;
    this.user = null;
    
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  }

  /**
   * Check if user is authenticated
   * @returns {boolean}
   */
  isAuthenticated() {
    return !!this.token;
  }

  /**
   * Get current user
   * @returns {Object|null}
   */
  getCurrentUser() {
    return this.user;
  }

  /**
   * Get auth token
   * @returns {string|null}
   */
  getToken() {
    return this.token;
  }

  /**
   * Get axios instance with auth headers
   * @returns {Object}
   */
  getAuthAxios() {
    return axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Authorization': `Bearer ${this.token}`
      }
    });
  }
}

const authService = new AuthService();
export default authService;
